"use client";

import Image from "next/image";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { featuresSlideshow } from "@/lib/constants";

export default function FeaturesSlideshow() {
  const [currentIndex, setCurrentIndex] = useState(0);

  function nextSlide() {
    setCurrentIndex((prev) => (prev + 1) % featuresSlideshow.length);
  }
  function prevSlide() {
    setCurrentIndex(
      (prev) =>
        (prev - 1 + featuresSlideshow.length) % featuresSlideshow.length,
    );
  }
  function getVisibleSlides() {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      visible.push(
        featuresSlideshow[(currentIndex + i) % featuresSlideshow.length],
      );
    }
    return visible;
  }

  return (
    <section
      id="features"
      className="bg-card border-b border-border py-20 md:py-28 relative overflow-hidden"
    >
      {/* Background image — no gradient, just low opacity */}
      <div className="absolute inset-0 z-0">
        <Image
          src="/features.jpg"
          alt=""
          fill
          className="object-cover opacity-8"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div>
            <div className="w-12 h-1 bg-primary mb-5" />
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-foreground uppercase tracking-tight">
              Features
            </h2>
            <p className="font-sans text-muted-foreground mt-2">
              See what makes us different
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={prevSlide}
              className="w-11 h-11 border-2 border-border flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors duration-200"
              aria-label="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <div className="flex gap-1.5">
              {featuresSlideshow.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`h-2 transition-all duration-200 ${
                    idx === currentIndex
                      ? "w-6 bg-primary"
                      : "w-2 bg-border hover:bg-muted-foreground"
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="w-11 h-11 border-2 border-border flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors duration-200"
              aria-label="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Slides */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {getVisibleSlides().map((slide, i) => (
            <div
              key={slide.id + "-" + i}
              className="card-lift bg-background border border-border overflow-hidden"
            >
              <div className="relative h-52 w-full overflow-hidden">
                <Image
                  src={slide.image}
                  alt={slide.title}
                  fill
                  className="object-cover"
                />
                {/* thin orange top border accent */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-extrabold text-foreground uppercase tracking-tight mb-3">
                  {slide.title}
                </h3>
                <div className="font-sans text-sm text-muted-foreground leading-relaxed">
                  {slide.content}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
